/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Component, Fragment } from 'react'

import type { WithEuiThemeProps } from '@elastic/eui'
import {
  EuiSpacer,
  EuiCheckbox,
  EuiFormHelpText,
  EuiOverlayMask,
  EuiModal,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiModalBody,
  EuiModalFooter,
  withEuiTheme,
  htmlIdGenerator,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { Allocator } from '@modules/ui-types'
import { CuiAlert } from '@modules/cui/Alert'
import { CuiButton } from '@modules/cui/Button'
import PermissionsGate from '@modules/permissions-components/PermissionsGate'

import type { DeleteAllocatorState } from '@/types/redux'

type Props = {
  allocator: Allocator
  deleteAllocatorInformation?: DeleteAllocatorState
  deleteAllocator: (removeInstances: boolean) => void
  close: () => void
} & WithEuiThemeProps

type State = {
  removeInstances: boolean
}

class DeleteAllocatorModal extends Component<Props, State> {
  state: State = {
    removeInstances: false,
  }

  render() {
    const { close, theme } = this.props
    const modalTitleId = htmlIdGenerator()('modalTitle')

    return (
      <EuiOverlayMask>
        <EuiModal
          aria-labelledby={modalTitleId}
          onClose={close}
          style={{ maxWidth: theme.euiTheme.breakpoint.xl }}
        >
          <EuiModalHeader>
            <EuiModalHeaderTitle id={modalTitleId}>
              <FormattedMessage
                id='delete-allocator-modal.delete-allocator'
                defaultMessage='Delete allocator'
              />
            </EuiModalHeaderTitle>
          </EuiModalHeader>

          <EuiModalBody>
            <div>{this.renderModalBody()}</div>
          </EuiModalBody>

          <EuiModalFooter>
            <div>{this.renderModalFooter()}</div>
          </EuiModalFooter>
        </EuiModal>
      </EuiOverlayMask>
    )
  }

  renderModalBody() {
    const { removeInstances } = this.state

    return (
      <EuiCheckbox
        id='set-allocator-remove-instances'
        label={
          <Fragment>
            <FormattedMessage
              id='allocator-delete-index.remove-instances'
              defaultMessage='Remove instances'
            />

            <EuiFormHelpText>
              <FormattedMessage
                id='allocator-delete-index.remove-all-instances-associated-with-this-allocator'
                defaultMessage='This option removes all instances associated with this allocator.'
              />
            </EuiFormHelpText>
          </Fragment>
        }
        checked={removeInstances}
        onChange={() => this.toggleRemoveInstances()}
      />
    )
  }

  renderModalFooter() {
    const { allocator, deleteAllocatorInformation = {}, deleteAllocator } = this.props
    const { removeInstances } = this.state
    const { connected } = allocator

    return (
      <Fragment>
        <PermissionsGate
          permissions={[
            {
              type: 'allocator',
              action: 'delete',
              id: allocator.id,
            },
          ]}
        >
          {({ hasPermissions }) => (
            <CuiButton
              color='danger'
              onClick={() => deleteAllocator(removeInstances)}
              spin={deleteAllocatorInformation.isDeleting}
              fill={true}
              disabled={!hasPermissions || connected}
            >
              <FormattedMessage id='delete-allocator.delete' defaultMessage='Delete allocator' />
            </CuiButton>
          )}
        </PermissionsGate>

        {deleteAllocatorInformation.error && (
          <Fragment>
            <EuiSpacer size='m' />

            <CuiAlert
              data-test-id='delete-allocator-error'
              type='error'
              details={deleteAllocatorInformation.error}
            >
              <FormattedMessage
                id='allocator-delete.button'
                defaultMessage='Deleting allocator failed'
              />
            </CuiAlert>
          </Fragment>
        )}
      </Fragment>
    )
  }

  toggleRemoveInstances() {
    this.setState({
      removeInstances: !this.state.removeInstances,
    })
  }
}

export default withEuiTheme(DeleteAllocatorModal)
